/**
 * @TODO-DP: this whole component is quite a mess, it has been resurrected from the old autosourcing implementation
 * Ideally, we should put autosourcing onto a company settings, move rules management to GraphQL, and use Apollo + React Hooks Form to manage them
 */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Helmet from 'react-helmet';
import {
  Button,
  Typography,
  Grid,
  FormControl,
  LinearProgress,
  Select,
  InputLabel,
  MenuItem,
  Checkbox,
  ListItemText,
  Input,
} from '@material-ui/core';
import _ from 'lodash';

import { selectFirstAutoSourcingRule } from 'entities/autoSourcingRule/selectors';
import { useCategories } from 'entities/cause/api';
import { selectAccessibleUserLocations, selectUser } from 'entities/user/selectors';
import Box from 'retired/Box';
import OrganizationMenu from 'retired/OrganizationMenu';
import FormLocationSelector from 'retired/FormLocationSelector';
import { useInjectEpics } from 'utils/injectEpics';
import { useInjectReducer } from 'utils/injectReducer';
import { Features } from 'utils/features';
import { Privileges } from 'utils/privileges';
import { opportunityProviderLabelsMap } from 'shared/opportunityProviders';
import { OpportunityAttendence, OpportunityProvider, OpportunityType } from 'generated/graphql';

import epics from './epics';
import reducer from './reducer';
import { initAction, submitAction } from './types';
import { selectLoading, selectSubmitting } from './selectors';

const opportunityAttendenceMap = {
  [OpportunityAttendence.InPerson]: 'In Person',
  [OpportunityAttendence.Virtual]: 'Virtual',
};
const opportunityTypes = Object.keys(OpportunityType);

const AutoSourcingPage = () => {
  const dispatch = useDispatch();
  useInjectEpics({ key: 'autoSourcingPage', epics });
  useInjectReducer({ key: 'autoSourcingPage', reducer });

  const history = useHistory();
  const user = useSelector(selectUser);
  const locations = useSelector(selectAccessibleUserLocations);
  const categories = useCategories();
  const loading = useSelector(selectLoading);
  const submitting = useSelector(selectSubmitting);
  const rule = useSelector(selectFirstAutoSourcingRule);

  const [locationIds, setLocationIds] = useState([]);
  const [categoryIds, setCategoryIds] = useState([]);
  const [providers, setProviders] = useState([]);
  const [types, setTypes] = useState([]);
  const [attendence, setAttendence] = useState<'all' | 'InPerson' | 'Virtual'>('all');

  const disabledOpportunityProviders = [
    OpportunityProvider.Goodera,
    ...((user?.organization?.getIn(['settings', 'disabledOpportunityProviders']) ||
      []) as string[]),
  ];

  useEffect(() => {
    if (
      user &&
      (!user.hasFeature(Features.AUTO_SOURCE_DEEDS) ||
        !user.hasPrivilege(Privileges.AUTO_SOURCE_MANAGE))
    ) {
      history.replace('/');
    }
  });

  useEffect(() => {
    dispatch(initAction());
  }, []);

  useEffect(() => {
    if (rule && user) {
      setLocationIds(rule.locations.toJS());
      setCategoryIds(rule.categories.toJS());
      setProviders(
        rule.providers
          .toJS()
          .filter((providerKey) => !disabledOpportunityProviders.includes(providerKey))
      );
      setTypes(rule.types.toJS());
      setAttendence(rule.attendence || 'all');
    }
  }, [rule, user]);

  const submitData = () => {
    const data = {
      locations: locationIds,
      categories: categoryIds,
      providers,
      types,
      attendence: attendence === 'all' ? null : attendence,
    };
    if (rule) {
      dispatch(submitAction(rule.merge(data)));
    } else {
      dispatch(submitAction(data));
    }
  };

  const enabledOpportunityProviders = _.omit(
    opportunityProviderLabelsMap,
    ...disabledOpportunityProviders
  );

  const setting =
    Boolean(locations.size && categories.length && rule) &&
    `Volunteering opportunities are sourced from ${
      providers.length === Object.keys(enabledOpportunityProviders).length || providers.length === 0
        ? 'all partners'
        : providers.slice(0, -1).join(', ') +
          (providers.length > 1 ? ' & ' : ' ') +
          providers.slice(-1)
    }, from ${
      locationIds.length === locations.length || locationIds.length === 0
        ? 'all locations'
        : `location${locationIds.length > 1 ? 's' : ''} ${
            locationIds
              .map((id) => locations.get(id).name)
              .slice(0, -1)
              .join(', ') +
            (locationIds.length > 1 ? ' & ' : ' ') +
            locationIds.map((id) => locations.get(id).name).slice(-1)
          }`
    }, ${types.length === 0 ? 'all opportunity types' : `of type ${types.join(', ')}`}, with ${
      attendence && attendence !== 'all'
        ? `${opportunityAttendenceMap[attendence]} attendence`
        : 'all attendence types'
    } and in ${
      categoryIds.length === categories.length || categoryIds.length === 0
        ? 'all cause areas'
        : `cause area${categoryIds.length > 1 ? 's' : ''} ${
            categoryIds
              .map((id) => categories.find((category) => category._id === id).name)
              .slice(0, -1)
              .join(', ') +
            (categoryIds.length > 1 ? ' & ' : ' ') +
            categoryIds
              .map((id) => categories.find((category) => category._id === id).name)
              .slice(-1)
          }`
    }`;

  return (
    <>
      {user?.hasPrivilege(Privileges.ORGANIZATION_MANAGE_SETTINGS) && (
        <div style={{ marginBottom: 60 }}>
          <OrganizationMenu user={user} />
        </div>
      )}
      {loading ? (
        <div style={{ maxWidth: 1200 }}>
          <LinearProgress />
        </div>
      ) : (
        <Box style={{ maxWidth: 1200 }}>
          <Helmet title="User Settings" />
          <h2>Auto-sourcing Volunteering Opportunities Settings</h2>
          <p>
            Deed is integrated with multiple volunteering platforms to provide you with access to a
            wide range of opportunities to source for your volunteering programs. Below you can set
            a rule that automatically pulls opportunities into the app that align with your
            programs.
          </p>

          {setting && (
            <div style={{ display: 'flex', marginBottom: 15 }}>
              <p style={{ fontWeight: 'bold', minWidth: 130 }}>Active setting:</p> <p>{setting}</p>
            </div>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <InputLabel shrink>Select Locations</InputLabel>
              <FormLocationSelector
                input={{
                  onChange: (value: string[]): void => setLocationIds(value.toArray()),
                  value: locationIds,
                }}
                locations={locations}
                placeholder="All are selected by default"
                style={{ marginRight: 10 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl style={{ width: '100%' }}>
                <InputLabel id="category" shrink>
                  Select Cause Areas
                </InputLabel>
                <Select
                  labelId="category"
                  value={categoryIds}
                  onChange={(e) =>
                    e.target.value.includes('all')
                      ? setCategoryIds([])
                      : setCategoryIds(e.target.value)
                  }
                  style={{ maxWidth: 300 }}
                  renderValue={(selected) =>
                    selected.length && categories.length
                      ? selected
                          .map(
                            (id: string) => categories.find((category) => category._id === id).name
                          )
                          .join(', ')
                      : 'All'
                  }
                  input={<Input />}
                  multiple
                  displayEmpty
                >
                  <MenuItem value="all">All</MenuItem>
                  {categories.map((category) => (
                    <MenuItem value={category._id} key={category._id}>
                      <Checkbox color="primary" checked={categoryIds.includes(category._id)} />
                      <ListItemText primary={category.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl style={{ width: '100%' }}>
                <InputLabel id="partner" shrink>
                  Select Partner
                </InputLabel>
                <Select
                  labelId="partner"
                  value={providers}
                  onChange={(e) =>
                    e.target.value.includes('all') ? setProviders([]) : setProviders(e.target.value)
                  }
                  style={{ maxWidth: 300 }}
                  renderValue={(selected) => (selected.length ? selected.join(', ') : 'All')}
                  input={<Input />}
                  multiple
                  displayEmpty
                >
                  <MenuItem value="all">All</MenuItem>
                  {Object.keys(enabledOpportunityProviders).map((providerId) => (
                    <MenuItem value={providerId} key={providerId}>
                      <Checkbox color="primary" checked={providers.includes(providerId)} />
                      <ListItemText primary={enabledOpportunityProviders[providerId]} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl style={{ width: '100%' }}>
                <InputLabel id="attendence" shrink>
                  Select Attendence
                </InputLabel>
                <Select
                  labelId="attendence"
                  value={attendence}
                  onChange={(e) => setAttendence(e.target.value)}
                  style={{ maxWidth: 300 }}
                  input={<Input />}
                  displayEmpty
                >
                  <MenuItem value="all">All</MenuItem>
                  {Object.keys(opportunityAttendenceMap).map((attendenceType) => (
                    <MenuItem value={attendenceType}>
                      {opportunityAttendenceMap[attendenceType]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl style={{ width: '100%' }}>
                <InputLabel id="types" shrink>
                  Select Opportunity Type
                </InputLabel>
                <Select
                  labelId="types"
                  value={types}
                  onChange={(e) => setTypes(e.target.value)}
                  style={{ maxWidth: 300 }}
                  renderValue={(selected) => (selected.length ? selected.join(', ') : 'All')}
                  input={<Input />}
                  multiple
                  displayEmpty
                >
                  {opportunityTypes.map((type) => (
                    <MenuItem value={type} key={type}>
                      <Checkbox color="primary" checked={types.includes(type)} />
                      <ListItemText primary={type} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Typography align="right">
            <Button
              color="primary"
              onClick={submitData}
              disabled={submitting || loading}
              variant="contained"
              style={{ marginTop: 30 }}
            >
              Submit
            </Button>
          </Typography>
        </Box>
      )}
    </>
  );
};

export default AutoSourcingPage;
